/**
 * The asset's Ref Finance DCL pools: what they offer, and what has traded.
 *
 * Kept apart from the NEAR Intents market on purpose. A user who buys through
 * 1Click is filled by a solver (a trade on that market), and the solver then
 * restocks from this pool (a swap here). Both are real, and summing them would
 * count the same demand twice — so they are two markets and never one volume.
 *
 * The pool contract is on a shard our node does not track, so its state is
 * read from a public endpoint and its swaps are found on intents.near's shard
 * and fetched whole. A swap made by calling the pool directly is not read, and
 * the coverage row says exactly how much of the pool's own recorded volume
 * that leaves out (blackbeard/observer#37).
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import type { IntentsSeries } from '../api/generated/IntentsSeries'
import type { RefPools } from '../api/generated/RefPools'
import type { RefSwap } from '../api/generated/RefSwap'
import type { RefSwaps } from '../api/generated/RefSwaps'
import { RequestFailed, fetchRefPools, fetchRefSeries, fetchRefSwaps } from '../api/rest'
import {
  ago,
  bucketWidth,
  height as fmtHeight,
  price as fmtPrice,
  tokens,
  utc,
  when,
  zone,
} from '../lib/format'
import { href } from '../lib/routes'
import { Field } from './Field'
import { BidSidePanel } from './BidSidePanel'
import { RefV1Panel } from './RefV1Panel'
import { SeriesBody } from './PriceChart'

const REFRESH_MS = 60_000

/** A share as a percentage, or a dash where there is nothing to divide. */
function pct(part: string, whole: string): string {
  const w = Number(whole)
  if (!w) return '—'
  return `${((Number(part) / w) * 100).toFixed(2)}%`
}

/** How each kind of rung reads in the table. */
const KIND: Record<string, string> = {
  asks: 'offers the asset',
  bids: 'bids for the asset',
  current: 'the current point',
}

/**
 * A rung's description. A limit order says so: its owner can cancel it at any
 * moment, where liquidity moves only when its provider withdraws, and a reader
 * deciding when to sell needs to know which they are counting on
 * (blackbeard/observer#41).
 */
function describe(kind: string, source: string): string {
  const what = KIND[kind] ?? kind
  return source === 'order' ? `limit order: ${what}` : what
}

/** Whole units with a few decimals, or a dash for nothing. */
function amountOrDash(v: number, places: number): string {
  return v > 0 ? v.toLocaleString('en-US', { maximumFractionDigits: places }) : '—'
}

export function RefDclMarket({
  chain,
  decimals,
  symbol,
}: {
  chain: string
  decimals: number
  symbol: string
}) {
  const [pools, setPools] = useState<RefPools | null | undefined>(undefined)
  const [swaps, setSwaps] = useState<RefSwaps | null>(null)
  const [older, setOlder] = useState<RefSwap[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let controller = new AbortController()
    const load = () => {
      controller = new AbortController()
      Promise.all([
        fetchRefPools(chain, controller.signal),
        fetchRefSwaps(chain, null, controller.signal),
      ])
        .then(([p, s]) => {
          setPools(p)
          setSwaps(s)
          setError(null)
        })
        .catch((e: unknown) => {
          if (controller.signal.aborted) return
          setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
        })
    }
    setOlder([])
    load()
    const id = window.setInterval(load, REFRESH_MS)
    return () => {
      window.clearInterval(id)
      controller.abort()
    }
  }, [chain])

  const asset = pools?.asset_symbol ?? swaps?.asset_symbol ?? symbol
  const assetDecimals = pools?.asset_decimals ?? swaps?.asset_decimals ?? decimals
  const cursor = swaps?.next ?? null
  const loadOlder = () => {
    if (!cursor) return
    fetchRefSwaps(chain, cursor)
      .then((page) => {
        setOlder((rows) => [...rows, ...page.swaps])
        setSwaps((s) => (s ? { ...s, next: page.next } : page))
      })
      .catch(() => {})
  }
  const rows = swaps ? [...swaps.swaps, ...older] : []

  return (
    <>
      <p className="panel-note" style={{ marginBottom: 12 }}>
        <Link to={href({ chain, index: 'market' })}>Markets</Link> › Ref Finance DCL
      </p>

      {error && <p className="empty">{error}</p>}
      {!error && pools === undefined && <p className="empty">Reading the pools…</p>}
      {!error && pools === null && (
        <p className="empty">
          Not read yet — the first read is a minute after the observer starts.
        </p>
      )}
      {pools && pools.pools.length === 0 && <p className="empty">No Ref DCL pool holds {asset}.</p>}

      {pools?.pools.map((p) => {
        const counter = p.counter_symbol ?? p.counter_token
        const amount = (raw: string, d: number) => tokens(raw, d)
        return (
          <section key={p.pool_id} className="panel" style={{ marginBottom: 26 }}>
            <div className="panel-head">
              <h2 className="panel-title">
                {asset}/{counter} pool
              </h2>
              <span className="eyebrow">
                {p.contract} · {(p.fee / 10_000).toFixed(2)}% fee · {p.state} · read{' '}
                {ago(pools.checked_at)}
              </span>
            </div>
            <div className="node-grid">
              <Field
                label="Pool price"
                value={`${fmtPrice(p.price)} ${counter}`}
                note="where the next swap starts — not a trade"
              />
              <Field
                label={`${asset} in the pool`}
                value={`${amount(p.asset_held, assetDecimals)} ${asset}`}
                note="what buyers can take"
              />
              <Field
                label={`${counter} in the pool`}
                value={`${tokens(p.counter_held, 6)} ${counter}`}
                note="what sellers can be paid, and LP fees"
                title={`${p.counter_held} base units`}
              />
              <Field
                label="Bids in limit orders"
                value={`${amountOrDash(
                  p.rungs.filter((r) => r.source === 'order').reduce((n, r) => n + r.counter, 0),
                  2,
                )} ${counter}`}
                note="posted, and cancellable at any moment"
              />
              <Field
                label="Swaps indexed"
                value={fmtHeight(p.indexed_swaps)}
                note={`${pct(p.indexed_asset_out, p.asset_out)} of ${asset} bought out, ${pct(p.indexed_asset_in, p.asset_in)} sold in`}
              />
            </div>
            <div className="scroll-x">
              <table className="board">
                <caption className="visually-hidden">The pool&apos;s liquidity ladder</caption>
                <thead>
                  <tr>
                    <th scope="col" className="left">
                      Price of {asset}
                    </th>
                    <th scope="col" className="left">
                      This range
                    </th>
                    <th scope="col">{asset}</th>
                    <th scope="col">{counter}</th>
                    <th scope="col">
                      {counter} to take all its {asset}
                    </th>
                    <th scope="col">{asset} it absorbs</th>
                  </tr>
                </thead>
                <tbody>
                  {p.rungs.map((r) => (
                    <tr key={`${r.price_from}-${r.price_to}-${r.kind}-${r.source}`}>
                      <td className="left numeral">
                        {r.kind === 'current' || r.source === 'order'
                          ? fmtPrice(r.price_from)
                          : `${fmtPrice(r.price_from)} – ${fmtPrice(r.price_to)}`}
                      </td>
                      <td className="left">{describe(r.kind, r.source)}</td>
                      <td className="numeral">
                        {r.asset > 0
                          ? r.asset.toLocaleString('en-US', { maximumFractionDigits: 3 })
                          : '—'}
                      </td>
                      <td className="numeral">
                        {r.counter > 0
                          ? r.counter.toLocaleString('en-US', { maximumFractionDigits: 4 })
                          : '—'}
                      </td>
                      <td className="numeral">
                        {r.counter_to_clear > 0
                          ? r.counter_to_clear.toLocaleString('en-US', {
                              maximumFractionDigits: 0,
                            })
                          : '—'}
                      </td>
                      <td className="numeral">{amountOrDash(r.asset_to_clear, 1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="panel-note">
              The ladder is everything the pool would trade against: its liquidity, range by range,
              and its limit orders, one price each. A row above the current price offers {asset};
              one below it bids for {asset}, and &ldquo;absorbs&rdquo; is how much {asset} a seller
              could sell into it, before the pool&apos;s fee. A limit order can be cancelled by
              whoever posted it at any moment, so a bid that is there now may not be there when
              someone sells. These are standing amounts, not trades — and{' '}
              <strong>
                “swaps indexed” is measured against the pool&apos;s own cumulative counters
              </strong>
              : anything under 100% is volume this observer did not read — a direct call to the pool
              signed on a shard none of our nodes follows, or made before they synced. Accounts
              allowed to swap here: {p.whitelist.join(', ')}.
            </p>
          </section>
        )
      })}

      {pools?.pools.map((p) => (
        <BidSidePanel
          key={`bids-${p.pool_id}`}
          chain={chain}
          poolId={p.pool_id}
          refresh={pools.checked_at}
        />
      ))}

      {pools?.pools.map((p) => (
        <RefPriceChart
          key={`chart-${p.pool_id}`}
          chain={chain}
          counter={p.counter_token}
          counterSymbol={p.counter_symbol}
          asset={asset}
          refresh={pools.checked_at}
        />
      ))}

      {pools && pools.pools.length > 0 && (
        <section className="panel" style={{ marginBottom: 26 }}>
          <div className="panel-head">
            <h2 className="panel-title">Pool swaps</h2>
            <span className="eyebrow">newest first</span>
          </div>
          {rows.length === 0 ? (
            <p className="empty">No swap in these pools has been indexed yet.</p>
          ) : (
            <div className="scroll-x">
              <table className="board">
                <caption className="visually-hidden">Swaps in the pools, newest first</caption>
                <thead>
                  <tr>
                    <th scope="col" className="left">
                      Settled
                    </th>
                    <th scope="col" className="left">
                      Solver
                    </th>
                    <th scope="col" className="left">
                      Side
                    </th>
                    <th scope="col">{asset}</th>
                    <th scope="col">Price</th>
                    <th scope="col" className="left">
                      Tx
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((s) => (
                    <tr key={`${s.tx_hash}-${s.near_height}-${s.asset_amount}`}>
                      <td className="left numeral" title={utc(s.at)}>
                        {when(s.at)}
                      </td>
                      <td className="left">{s.trader}</td>
                      <td className="left">{s.side === 'buy' ? 'bought' : 'sold'}</td>
                      <td className="numeral">
                        {tokens(s.asset_amount, assetDecimals)} {asset}
                      </td>
                      <td className="numeral">{fmtPrice(s.price)}</td>
                      <td className="left">
                        <Link to={href({ chain, nearTx: s.tx_hash })} title={s.tx_hash}>
                          {s.tx_hash.slice(0, 8)}…
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {cursor && (
            <div className="pager">
              <button className="segmented panel-button" onClick={loadOlder}>
                Older →
              </button>
            </div>
          )}
          <p className="panel-note">
            A pool swap is a solver trading with the pool — typically restocking after filling a
            user on NEAR Intents, which is why these are not added to that market&apos;s trades. The
            price includes the pool&apos;s fee. The solver is named from its own intent; the pool
            itself records the swapper as intents.near.
          </p>
          <p className="panel-note">What is read: {pools.reads}</p>
        </section>
      )}
      {pools?.asset && (
        <RefV1Panel
          chain={chain}
          assetContract={pools.asset.replace(/^nep141:/, '')}
          decimals={assetDecimals}
          symbol={asset}
        />
      )}
    </>
  )
}

/**
 * The pool's price over its latest swaps, against one counter token.
 *
 * Its own chart, never a second line on the NEAR Intents one: a pool swap is
 * usually a solver restocking after an intents fill, so one line over both
 * would draw one demand twice. The price is what the swap paid, fee included,
 * which is why it sits a fee's width from the pool price above.
 */
function RefPriceChart({
  chain,
  counter,
  counterSymbol,
  asset,
  refresh,
}: {
  chain: string
  counter: string
  counterSymbol: string | null
  asset: string
  refresh: string
}) {
  const [series, setSeries] = useState<IntentsSeries | null>(null)
  const [error, setError] = useState<string | null>(null)
  const unit = counterSymbol ?? counter

  useEffect(() => {
    const controller = new AbortController()
    fetchRefSeries(chain, counter, controller.signal)
      .then((s) => {
        setSeries(s)
        setError(null)
      })
      .catch((e: unknown) => {
        if (controller.signal.aborted) return
        setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
      })
    return () => controller.abort()
  }, [chain, counter, refresh])

  const drawn = series !== null && series.points.length > 0
  return (
    <section className="panel" style={{ marginBottom: 26 }}>
      <div className="panel-head">
        <h2 className="panel-title">Pool swap price</h2>
        <span className="eyebrow">
          {drawn
            ? `${asset} in ${unit} · latest ${fmtHeight(series.trades)} pool swaps · ${bucketWidth(series.bucket_seconds)} buckets · times in ${zone()}`
            : `${asset} in ${unit}`}
        </span>
      </div>
      <SeriesBody
        series={series}
        unit={unit}
        asset={asset}
        error={error}
        empty={
          series === null
            ? 'Reading…'
            : `The chart starts with the first swap in this pool that the observer has read. None has yet — the pool price above is where the next one would start, not a trade.`
        }
      />
      <p className="panel-note">
        Each point is one time bucket: the volume-weighted price of the pool swaps in it, fee
        included, in {unit}. The line breaks where nothing traded and the vertical axis is fitted to
        the prices, not anchored at zero. These are solvers trading with the pool, mostly restocking
        after a NEAR Intents fill — a separate market, deliberately not drawn on that chart. A swap
        made by calling the pool directly is not read; the coverage figure above says how much of
        the pool&apos;s volume that leaves out.
      </p>
    </section>
  )
}

/**
 * The pool's swap price for a page that is not the pool's own: the NEAR
 * Intents market, where 1Click fills carry no price and this is the price the
 * solver pays to restock (blackbeard/observer#43).
 */
export function PoolPriceChart({ chain, symbol }: { chain: string; symbol: string }) {
  const [pools, setPools] = useState<RefPools | null>(null)
  useEffect(() => {
    const controller = new AbortController()
    fetchRefPools(chain, controller.signal)
      .then(setPools)
      .catch(() => {})
    return () => controller.abort()
  }, [chain])
  const pool = pools?.pools[0]
  if (!pools || !pool) return null
  return (
    <RefPriceChart
      chain={chain}
      counter={pool.counter_token}
      counterSymbol={pool.counter_symbol}
      asset={pools.asset_symbol ?? symbol}
      refresh={pools.checked_at}
    />
  )
}
